import { useMediaQuery } from 'react-responsive';

import Button from 'honeydew/Button';
import { Drawer } from 'honeydew/Drawer';
import Modal from 'honeydew/Modal';
import useTailwindTheme from 'hooks/useTailwindTheme';

/**
 * @param {Object} props - Component props
 * @param {number} props.frozenFee - The frozen packaging fee amount to display
 * @param {number} [props.frozenFeeWaiveThreshold] - The threshold to waive the frozen packaging fee
 * @param {Function} props.handleClose - Function to call when the modal should be closed
 * @param {boolean} props.open - Whether the modal is currently open
 */
function FrozenPackagingModal({ frozenFee, frozenFeeWaiveThreshold, handleClose, open }) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    return mobile ? (
        <Drawer
            anchor="bottom"
            open={open}
            PaperProps={{ sx: { borderRadius: '20px 20px 0 0', padding: '20px' } }}
            toggleDrawer={() => handleClose()}
            data-testid="frozen-packaging-fee-modal"
        >
            <div className="mb-20 flex flex-col gap-10">
                <ModalHeading frozenFee={frozenFee} />
                <ModalContent frozenFeeWaiveThreshold={frozenFeeWaiveThreshold} />
            </div>
            <Button onClick={() => handleClose()}>Got it</Button>
        </Drawer>
    ) : (
        <Modal
            data-testid="frozen-packaging-fee-modal"
            headline={<ModalHeading frozenFee={frozenFee} />}
            open={open}
            onClose={() => handleClose()}
        >
            <ModalContent frozenFeeWaiveThreshold={frozenFeeWaiveThreshold} />
        </Modal>
    );
}

function ModalHeading({ frozenFee }) {
    return (
        <h2 className="text-body-md font-grotesk-bold">
            <div className="flex items-center justify-between gap-5">
                <div>Frozen Packaging Fee</div>
                {frozenFee > 0 && <div>${frozenFee.toFixed(2)}</div>}
            </div>
        </h2>
    );
}

function ModalContent({ frozenFeeWaiveThreshold }) {
    return (
        <p
            className="text-body-mobile-xs text-grey md:text-body-mobile-sm"
            data-testid="frozen-packaging-fee-modal-content"
        >
            Frozen Packaging Fee applies to frozen items. These items ship with dry ice and special packaging to keep
            them frozen in transit.{' '}
            {frozenFeeWaiveThreshold > 0 && (
                <>Fee will be waived if ${frozenFeeWaiveThreshold.toFixed(2)}+ of frozen items are ordered.</>
            )}
        </p>
    );
}

export default FrozenPackagingModal;
